import { useSelector, useDispatch } from "react-redux";
import { setUser } from "store/auth/userSlice";
import { apiSignIn, apiSignUp } from "services/AuthService";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import appConfig from "configs/app.config";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";
import { Notification, toast } from "components/ui";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { setVarta } from "store/base/commonSlice";
import { apiLoginWithUser } from "services/UserService";
import getDomain from "utils/getDomain";
import { clearDashboardMetricsCache } from "utils/customerDashboardCache";
import { xiaomiDomains } from "constants/moduleConstants/domain.constant";

const XIAOMI_ENTRY_PATH = "/user-wastes/waste-list/add-waste";

function getAuthenticatedEntryPath() {
  const domain = getDomain().toLowerCase();
  if (xiaomiDomains.includes(domain)) {
    return XIAOMI_ENTRY_PATH;
  }
  return appConfig.authenticatedEntryPath;
}

/** API kullanıcı yanıtından Redux user payload; branches/roles eksik null olduğunda .map / .includes hatalarını önler */
function toSessionUserPayload(data) {
  if (!data) {
    return {
      avatar: "",
      userName: "Anonymous",
      phone: "",
      companies: [],
      authority: [],
      branches: [],
      isindividual: true,
      email: "",
      address: "",
    };
  }
  const rawBranches = data.branches;
  const branches = Array.isArray(rawBranches)
    ? rawBranches
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "object" && "branch" in item) {
            return item.branch;
          }
          return item;
        })
        .filter((b) => b != null)
    : [];
  const rawRoles = data.roles;
  const authority =
    rawRoles == null
      ? []
      : Array.isArray(rawRoles)
        ? rawRoles
        : [rawRoles];
  return {
    avatar: "",
    userName: data.fullname ?? "",
    phone: data.phone ?? "",
    companies: data.companies || [],
    authority,
    branches,
    isindividual: data.isindividual,
    email: data.email,
    status: data.status,
    address: data.address ?? "",
  };
}

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const token = useSelector((state) => state.auth?.session?.token);
  const signedIn = useSelector((state) => state.auth?.session?.signedIn);
  const signIn = async (values, resp) => {
    try {
      const resp = await apiSignIn(values);
      const userData = resp.data?.data;
      if (userData) {
        const { token } = userData;
        if (!token) {
          return {
            status: "failed",
            message: "Missing session token",
          };
        }
        dispatch(onSignInSuccess(token));
        dispatch(setUser(toSessionUserPayload(userData)));
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : getAuthenticatedEntryPath());
        return {
          status: "success",
          message: "",
        };
      }
      return {
        status: "failed",
        message: "Invalid sign-in response",
      };
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const signInWithOtp = async (resp) => {
    try {
      if (!resp?.data) {
        return { status: "failed", message: "Invalid sign-in response" };
      }
      const { token } = resp.data;
      if (!token) {
        return { status: "failed", message: "Missing session token" };
      }
      dispatch(onSignInSuccess(token));
      dispatch(setUser(toSessionUserPayload(resp.data)));
      const redirectUrl = query.get(REDIRECT_URL_KEY);
      navigate(redirectUrl ? redirectUrl : getAuthenticatedEntryPath());
      return {
        status: "success",
        message: "",
      };
    } catch (e) {
      console.error("signInWithOtp", e);
      return {
        status: "failed",
        message: e?.message || String(e),
      };
    }
  };

  const signInForUser = async (id) => {
    try {
      const resp = await apiLoginWithUser(id);
      const userData = resp.data?.data;
      if (userData) {
        const { token } = userData;
        if (!token) {
          return {
            status: "failed",
            message: "Missing session token",
          };
        }
        dispatch(onSignInSuccess(token));
        dispatch(setUser(toSessionUserPayload(userData)));
        setTimeout(() => {
          const redirectUrl = query.get(REDIRECT_URL_KEY);
          navigate(redirectUrl ? redirectUrl : getAuthenticatedEntryPath());
        }, 1000);
        return {
          status: "success",
          message: "",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data || errors.toString(),
      };
    }
  };

  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data.succeeded) {
        return {
          data: resp.data.data,
          status: "success",
        };
        // navigate("sign-in");
        // toast.push(
        //   <Notification
        //     title="Başarıyla Kayıt Oldunuz!"
        //     type="success"
        //     duration={3500}
        //     width={500}
        //   >
        //     Lütfen giriş yapınız.
        //   </Notification>,
        //   {
        //     placement: "top-center",
        //   }
        // );
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const handleSignOut = () => {
    clearDashboardMetricsCache();
    dispatch(onSignOutSuccess());
    // HubSpot script'i geç yüklenir veya engellenebilir; guard olmadan
    // TypeError atıp navigate'i hiç çalıştırmıyordu.
    try {
      window.HubSpotConversations?.clear?.({ resetWidget: true });
    } catch (e) {
      console.warn("HubSpot reset failed", e);
    }
    navigate("/sign-in");
  };

  const signOut = async () => {
    // There is no signout api right now.
    // await apiSignOut();
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signInForUser,
    signInWithOtp,
    signUp,
    signOut,
  };
}

export default useAuth;
